import {
  Box,
  Button,
  Chip,
  Collapse,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  tablePaginationClasses,
  visuallyHidden
} from "@outschool/backpack";
import {
  faChevronDown,
  faChevronUp,
  faDiamondExclamation
} from "@outschool/icons";
import { TFunction, useLocale, useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { useSectionListViewed } from "@outschool/ui-analytics";
import {
  getMeetingsForDisplay,
  useEsaAvailability
} from "@outschool/ui-components-website";
import { useIsTablet } from "@outschool/ui-utils";
import lodashRange from "lodash/range";
import React, { useCallback, useState } from "react";

import { MAX_BOOKABLE_FUTURE_WEEKS } from "../../enrolling/enrollmentForm/ChooseOngoingStartTime";
import LateEnrollmentMeetingListDescription from "./LateEnrollmentMeetingListDescription";
import {
  SectionRowActivity,
  SectionRowSection,
  SectionTableCta
} from "./types";

export const DESKTOP_ROWS_PER_PAGE = 7;

function useTableRowSx({
  shouldBlockEsaPurchase,
  isExpandingRow,
  isSellerOrg,
  isOngoing
}: {
  shouldBlockEsaPurchase?: boolean;
  isExpandingRow?: boolean;
  isSellerOrg?: boolean;
  isOngoing?: boolean;
}) {
  return React.useMemo(() => {
    // standardize row height to prevent UI jumping between the loading state and populated table
    // allow more room for seller org teacher names
    // for rows that expand, bottom padding comes from the expanded row, so we subtract that amount
    let rowHeight = isSellerOrg ? "109px" : "85px";
    if (isExpandingRow) {
      rowHeight = `calc(${rowHeight} - 16px)`;
    }
    return {
      "th, td": {
        height: isOngoing ? undefined : rowHeight,
        verticalAlign: "top",
        border: "none",
        paddingBottom: "1em",
        opacity: shouldBlockEsaPurchase ? 0.6 : null,
        cursor: shouldBlockEsaPurchase ? "not-allowed" : null
      },
      "&:not(:first-of-type)": {
        borderTop: "1px solid",
        borderTopColor: "neutral.200"
      }
    };
  }, [shouldBlockEsaPurchase, isExpandingRow, isSellerOrg, isOngoing]);
}

function LoadingRow({ isSellerOrg }: { isSellerOrg: boolean }) {
  const tableRowSx = useTableRowSx({ isSellerOrg });
  return (
    <TableRow sx={tableRowSx}>
      {lodashRange(0, 3).map(i => (
        <TableCell key={`cell-${i}`} align={i < 2 ? "left" : "right"}>
          <Box
            sx={{ display: "flex", justifyContent: i < 2 ? "left" : "right" }}
          >
            <Skeleton variant="text" height={50} width={200} />
          </Box>
        </TableCell>
      ))}
    </TableRow>
  );
}

function LoadingPagination() {
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "center",
        height: 52
      }}
    >
      <Skeleton
        variant="rectangular"
        height={24}
        width={140}
        sx={{ marginRight: "1em" }}
      />
    </Box>
  );
}

interface DesktopSectionTableProps {
  isLoading: boolean;
  activity: SectionRowActivity;
  sections: SectionRowSection[];
  totalCount: number;
  fetchNextPage: (offset: number) => void;
  isFlexSchedule: boolean;
  isOngoing: boolean;
  isOneTime: boolean;
  userTimeZone: string;
  isSellerOrg: boolean;
  CtaComponent: SectionTableCta;
}

export default function DesktopSectionTable({
  isLoading,
  activity,
  sections,
  totalCount,
  fetchNextPage,
  isFlexSchedule,
  isOngoing,
  isOneTime,
  isSellerOrg,
  userTimeZone,
  CtaComponent
}: DesktopSectionTableProps) {
  const locale = useLocale();
  const trackSectionListViewed = useSectionListViewed();
  const [page, setPage] = React.useState(0);

  function handleChangePage(_event: any, newPage: number) {
    setPage(newPage);
    fetchNextPage(newPage * DESKTOP_ROWS_PER_PAGE);
  }

  function pageLabel({ count, page }: { count: number; page: number }) {
    return `Page ${page + 1} of ${Math.ceil(count / DESKTOP_ROWS_PER_PAGE)}`;
  }

  const [expandedSectionUid, setExpandedSectionUid] = useState(null);
  const { t } = useTranslation(
    "client\\components\\detailPage\\availableTimes\\DesktopSectionTable"
  );
  const showTablePagination = totalCount > DESKTOP_ROWS_PER_PAGE;

  const SectionRow = isOngoing
    ? OngoingSectionRow
    : isFlexSchedule
    ? FlexSectionRow
    : isOneTime
    ? OneTimeSectionRow
    : MultiWeekSectionRow;

  if (!isLoading) {
    trackSectionListViewed({
      component: "DesktopSectionTable",
      page,
      rowsPerPage: DESKTOP_ROWS_PER_PAGE,
      sections,
      totalCount
    });
  }

  return (
    <>
      <TableContainer
        sx={{
          borderTop: "1px solid",
          borderTopColor: "neutral.200",
          borderBottom: "1px solid",
          borderBottomColor: "neutral.200",
          backgroundColor: "common.white"
        }}
      >
        <Table>
          <caption style={visuallyHidden}>
            {t("Table of available sections")}
          </caption>
          <TableBody>
            {isLoading
              ? lodashRange(0, DESKTOP_ROWS_PER_PAGE).map(i => (
                  <LoadingRow isSellerOrg={isSellerOrg} key={`row-${i}`} />
                ))
              : sections.map(section => (
                  <SectionRow
                    activity={activity}
                    section={section}
                    isOngoing={isOngoing}
                    userTimeZone={userTimeZone}
                    locale={locale}
                    isSellerOrg={isSellerOrg}
                    isExpanded={expandedSectionUid === section.uid}
                    setExpandedSectionUid={setExpandedSectionUid}
                    key={section.uid}
                    CtaComponent={CtaComponent}
                    t={t}
                  />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading ? (
        <LoadingPagination />
      ) : (
        showTablePagination && (
          <TablePagination
            component="div"
            rowsPerPage={DESKTOP_ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
            labelRowsPerPage=""
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={pageLabel}
            sx={{
              marginRight: "1em",
              // Workaround, TablePagination doesn't expose displayedRows slot props
              [`& .${tablePaginationClasses?.displayedRows}`]: {
                typography: "body1"
              }
            }}
          />
        )
      )}
    </>
  );
}

export function UnscheduledMeetingsNotice({
  section,
  meetings,
  t
}: {
  section: SectionRowSection;
  meetings: SectionRowSection["meetings"];
  t: TFunction;
}) {
  const numMeetings = meetings.length;
  const remainingMeetingCount = section?.progress?.remainingMeetingCount;
  const numUnscheduledMeetings = remainingMeetingCount
    ? remainingMeetingCount - numMeetings
    : undefined;
  return Boolean(numUnscheduledMeetings) ? (
    <Typography component="p" variant="body1">
      {t("{{numUnscheduledMeetings}} meeting not yet scheduled", {
        numUnscheduledMeetings,
        count: numUnscheduledMeetings,
        defaultValue_plural:
          "{{numUnscheduledMeetings}} meetings not yet scheduled"
      })}
    </Typography>
  ) : null;
}

interface SectionRowProps {
  activity: SectionRowActivity;
  section: SectionRowSection;
  isOngoing: boolean;
  userTimeZone: string;
  locale: string;
  isSellerOrg: boolean;
  isExpanded: boolean;
  setExpandedSectionUid: React.Dispatch<React.SetStateAction<string | null>>;
  CtaComponent: SectionTableCta;
  t: TFunction;
}

function OngoingSectionRow({
  activity,
  section,
  isOngoing,
  isSellerOrg,
  userTimeZone,
  locale,
  CtaComponent,
  t
}: SectionRowProps) {
  const { shouldBlockEsaPurchase, blockedEsaPurchaseMessage } =
    useEsaAvailability(activity, section);

  const meetings = getMeetingsForDisplay(isOngoing, section);

  const availableStartTimes = activity.allowsFutureOngoingEnrollments
    ? Math.min(meetings.length, MAX_BOOKABLE_FUTURE_WEEKS + 1)
    : 1;

  const meetingDays = Time.getDaysOfWeek({
    dateTimes: meetings.map(m => m.start_time),
    isOneTime: false,
    timeZone: userTimeZone,
    locale
  });
  const startTime = section.nextOngoingMeeting?.start_time;
  const tableRowSx = useTableRowSx({
    shouldBlockEsaPurchase,
    isSellerOrg,
    isOngoing: true
  });

  return (
    <>
      <TableRow sx={tableRowSx}>
        <TableCell sx={{ paddingX: 0 }}>
          <Box
            sx={(theme: Theme) => ({
              display: "grid",
              gridTemplateColumns: isSellerOrg ? "1fr 1fr 1fr" : "1fr 1fr",
              width: "100%",
              height: "100%",
              gap: 16,
              [theme.breakpoints.down(1068)]: {
                gridTemplateColumns: isSellerOrg ? "1fr 1fr" : undefined,
                gap: 8
              },
              [theme.breakpoints.down(900)]: {
                gridTemplateColumns: "1fr",
                gap: 8
              }
            })}
          >
            <Box
              sx={{
                minWidth: 120,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start"
              }}
            >
              <Typography
                component="div"
                variant="body1"
                data-test-id="desktop-str-meeting-date"
                emphasized={true}
              >
                {meetingDays}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                data-test-id="desktop-str-meeting-time"
              >
                {Time.briefDurationTimesString(
                  meetings[0].start_time,
                  meetings[0].end_time,
                  userTimeZone,
                  false
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                minWidth: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginLeft: 0
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start"
                }}
              >
                <DateSummary
                  section={section}
                  isOngoing={isOngoing}
                  startTime={startTime}
                  userTimeZone={userTimeZone}
                  endTime={null}
                  t={t}
                />
                {availableStartTimes > 1 && (
                  <Chip
                    color="primary"
                    size="small"
                    label={t("+{{availableStartTimes}} more start date", {
                      defaultValue_plural:
                        "+{{availableStartTimes}} more start dates",
                      availableStartTimes: availableStartTimes - 1,
                      count: availableStartTimes - 1
                    })}
                  />
                )}
              </Box>
            </Box>
            <TaughtBy
              section={section}
              isSellerOrg={isSellerOrg}
              isTeacherAssigned={activity.user_uid !== section.leader.uid}
              isOngoing={true}
              t={t}
            />
          </Box>
        </TableCell>
        <CTACell
          section={section}
          CtaComponent={CtaComponent}
          ctaDisabled={shouldBlockEsaPurchase}
          ctaDisabledMessage={blockedEsaPurchaseMessage}
          t={t}
          isOngoing={isOngoing}
        />
      </TableRow>
    </>
  );
}

function MultiWeekSectionRow({
  activity,
  section,
  isOngoing,
  isSellerOrg,
  userTimeZone,
  locale,
  isExpanded,
  setExpandedSectionUid,
  CtaComponent,
  t
}: SectionRowProps) {
  const { shouldBlockEsaPurchase, blockedEsaPurchaseMessage } =
    useEsaAvailability(activity, section);

  const onExpand = useCallback(() => {
    return setExpandedSectionUid(isExpanded ? null : section.uid);
  }, [setExpandedSectionUid, isExpanded, section.uid]);

  const meetings = getMeetingsForDisplay(isOngoing, section);

  const meetingDays = Time.getDaysOfWeek({
    dateTimes: meetings.map(m => m.start_time),
    isOneTime: false,
    timeZone: userTimeZone,
    locale
  });
  const numMeetings = meetings.length;
  const startTime = isOngoing
    ? section.nextOngoingMeeting?.start_time
    : meetings[0].start_time;
  const endTime = isOngoing ? null : meetings[numMeetings - 1].end_time;
  const tableRowSx = useTableRowSx({
    shouldBlockEsaPurchase,
    isExpandingRow: true,
    isSellerOrg
  });

  const isInProgress =
    !!section?.progress && section.progress.pastMeetingCount > 0;

  return (
    <>
      <TableRow sx={tableRowSx}>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "start",
              width: "100%",
              flexWrap: "wrap"
            }}
          >
            <Box
              sx={{
                width: "160px"
              }}
            >
              <Typography
                component="div"
                variant="h5"
                data-test-id="desktop-str-meeting-date"
              >
                {meetingDays}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                data-test-id="desktop-str-meeting-time"
              >
                {Time.briefDurationTimesString(
                  meetings[0].start_time,
                  meetings[0].end_time,
                  userTimeZone,
                  false
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "200px"
              }}
            >
              <DateSummary
                section={section}
                isOngoing={isOngoing}
                startTime={startTime}
                userTimeZone={userTimeZone}
                endTime={endTime}
                t={t}
              />
              <TaughtBy
                section={section}
                isSellerOrg={isSellerOrg}
                isTeacherAssigned={activity.user_uid !== section.leader.uid}
                t={t}
              />
              <Button
                onClick={onExpand}
                variant="link"
                endIcon={
                  <Icon
                    icon={isExpanded ? faChevronUp : faChevronDown}
                    sx={{
                      fontSize: "0.8em !important"
                    }}
                  />
                }
                sx={{
                  color: "primary.800",
                  fontWeight: "normal",
                  textDecoration: "none !important",
                  marginRight: 40
                }}
              >
                {isExpanded
                  ? isOngoing
                    ? t("Hide next {{numMeetings}} meetings", { numMeetings })
                    : isInProgress
                    ? t("Hide remaining {{numMeetings}} meetings", {
                        numMeetings: section?.progress?.remainingMeetingCount
                      })
                    : t("Hide all {{numMeetings}} meetings", { numMeetings })
                  : isOngoing
                  ? t("Show next {{numMeetings}} meetings", { numMeetings })
                  : isInProgress
                  ? t("Show remaining {{numMeetings}} meetings", {
                      numMeetings: section?.progress?.remainingMeetingCount
                    })
                  : t("Show all {{numMeetings}} meetings", { numMeetings })}
              </Button>
              <Collapse
                in={isExpanded}
                timeout="auto"
                unmountOnExit
                sx={{
                  marginTop: "1em"
                }}
              >
                {isInProgress && (
                  <LateEnrollmentMeetingListDescription
                    section={section}
                    t={t}
                    sx={{ py: 16 }}
                  />
                )}
                {meetings.map(
                  (meeting: SectionRowSection["meetings"][number]) => (
                    <Typography component="p" variant="body1" key={meeting.uid}>
                      {Time.fullDurationStringWithTimes(
                        meeting.start_time,
                        meeting.end_time,
                        userTimeZone,
                        true
                      )}
                    </Typography>
                  )
                )}
                <UnscheduledMeetingsNotice
                  section={section}
                  meetings={meetings}
                  t={t}
                />
              </Collapse>
            </Box>
          </Box>
        </TableCell>
        <CTACell
          section={section}
          CtaComponent={CtaComponent}
          ctaDisabled={shouldBlockEsaPurchase}
          ctaDisabledMessage={blockedEsaPurchaseMessage}
          t={t}
        />
      </TableRow>
    </>
  );
}

function DateSummary({
  section,
  isOngoing,
  startTime,
  endTime,
  userTimeZone,
  t
}: {
  section: SectionRowSection;
  isOngoing: boolean;
  startTime: any;
  endTime: any;
  userTimeZone: string;
  t: TFunction;
}) {
  return (
    <Box flex sx={{ flexWrap: "wrap", columnGap: "0.5em" }}>
      {!!section?.allowsLateEnrollment && !!section?.progress ? (
        <Box>
          <Typography
            variant="subtitle2"
            data-test-id="desktop-late-enrollment-warning"
            sx={{
              color: "error.700",
              paddingBottom: 8,
              verticalAlign: "text-top"
            }}
          >
            <Icon icon={faDiamondExclamation} sx={{ marginRight: 4 }} />
            {t("Started: ")} {Time.formatDate(section.start_time, userTimeZone)}
          </Typography>
          <Box>
            <Typography
              variant="body1"
              data-test-id="desktop-str-date-summary-start"
              sx={{ marginRight: "12px" }}
            >
              {t("Upcoming:")} {Time.formatDate(startTime, userTimeZone)}{" "}
              {t("(Week {{currentWeek}})", {
                currentWeek: section.progress?.currentWeek
              })}
            </Typography>
            <Box sx={{ paddingBottom: 8 }}>
              <Typography
                variant="body1"
                data-test-id="desktop-str-date-summary-end"
              >
                {t("Ends:")} {Time.formatDate(endTime, userTimeZone)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <Typography
            component={isOngoing ? "div" : "span"}
            variant="body1"
            emphasized={isOngoing}
            data-test-id="desktop-str-date-summary-start"
            sx={(theme: Theme) => ({
              color: isOngoing ? undefined : theme.palette.grey[600],
              display: isOngoing ? "block" : undefined
            })}
          >
            {isOngoing ? t("Next session:") : t("Starts:")}{" "}
            {Time.formatDate(startTime, userTimeZone)}
          </Typography>
          {endTime && (
            <Typography
              variant="body1"
              data-test-id="desktop-str-date-summary-end"
              sx={(theme: Theme) => ({
                color: theme.palette.grey[600]
              })}
            >
              {t("Ends:")} {Time.formatDate(endTime, userTimeZone)}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function FlexSectionRow({
  activity,
  section,
  isSellerOrg,
  userTimeZone,
  CtaComponent,
  t
}: SectionRowProps) {
  // calculate weeks remaining
  const startTimeObj = Time.dayjs(section.start_time);
  const todayObj = Time.dayjs();
  const hasFlexClassStarted = startTimeObj.isBefore(todayObj);
  const firstFlexDayObj = hasFlexClassStarted ? todayObj : startTimeObj;
  const flexTimeRemaining = Math.round(
    Time.dayjs(section.end_time).diff(firstFlexDayObj, "weeks", true)
  );
  const { shouldBlockEsaPurchase, blockedEsaPurchaseMessage } =
    useEsaAvailability(activity, section);
  const tableRowSx = useTableRowSx({ shouldBlockEsaPurchase, isSellerOrg });

  return (
    <TableRow sx={tableRowSx}>
      <TableCell>
        <Typography
          variant="h5"
          sx={{ display: "inline-block" }}
          data-test-id="desktop-str-meeting-date"
        >
          {Time.briefDurationStringWithoutTimes(
            section.start_time,
            section.end_time,
            userTimeZone
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          component="div"
          variant="body1"
          data-test-id="desktop-str-flex-time-start"
        >
          {hasFlexClassStarted ? t("Classroom opened") : t("Classroom opens:")}{" "}
          {hasFlexClassStarted
            ? null
            : t("{{openDate}} at {{openTime}}", {
                openDate: Time.formatDate(section.start_time, userTimeZone),
                openTime: Time.formatTime(
                  section.start_time,
                  userTimeZone,
                  false
                )
              })}
        </Typography>
        <TaughtBy
          section={section}
          isSellerOrg={isSellerOrg}
          isTeacherAssigned={activity.user_uid !== section.leader.uid}
          t={t}
        />
        <Typography
          component="div"
          variant="body1"
          data-test-id="desktop-str-flex-time-end"
        >
          {hasFlexClassStarted
            ? t("Remaining weeks: {{flexTimeRemaining}}", {
                flexTimeRemaining
              })
            : t("Total weeks: {{flexTimeRemaining}}", { flexTimeRemaining })}
        </Typography>
      </TableCell>
      <CTACell
        section={section}
        CtaComponent={CtaComponent}
        ctaDisabled={shouldBlockEsaPurchase}
        ctaDisabledMessage={blockedEsaPurchaseMessage}
        t={t}
        hideSeats={true}
      />
    </TableRow>
  );
}

function OneTimeSectionRow({
  activity,
  section,
  isSellerOrg,
  userTimeZone,
  CtaComponent,
  t
}: SectionRowProps) {
  const { shouldBlockEsaPurchase, blockedEsaPurchaseMessage } =
    useEsaAvailability(activity, section);
  const meeting = section.meetings[0];
  const tableRowSx = useTableRowSx({ shouldBlockEsaPurchase, isSellerOrg });

  return (
    <TableRow sx={tableRowSx}>
      <TableCell>
        <Typography
          variant="h5"
          component="div"
          data-test-id="desktop-str-meeting-date"
        >
          {Time.formatDateWithWeekday(meeting.start_time, userTimeZone)}
        </Typography>
        <Typography component="div" data-test-id="desktop-str-meeting-time">
          {Time.briefDurationTimesString(
            meeting.start_time,
            meeting.end_time,
            userTimeZone,
            false
          )}
        </Typography>
        <TaughtBy
          section={section}
          isSellerOrg={isSellerOrg}
          isTeacherAssigned={activity.user_uid !== section.leader.uid}
          t={t}
        />
      </TableCell>
      <CTACell
        section={section}
        CtaComponent={CtaComponent}
        ctaDisabled={shouldBlockEsaPurchase}
        ctaDisabledMessage={blockedEsaPurchaseMessage}
        t={t}
      />
    </TableRow>
  );
}

function CTACell({
  section,
  CtaComponent,
  ctaDisabled,
  ctaDisabledMessage,
  t,
  hideSeats,
  isOngoing
}: {
  CtaComponent: SectionTableCta;
  section: SectionRowSection;
  ctaDisabled: boolean;
  ctaDisabledMessage: string | null;
  t: TFunction;
  hideSeats?: boolean;
  isOngoing?: boolean;
}) {
  const numOpenings = section.size_max
    ? section.size_max - section.filledSpaceCount
    : 0;
  const showNumOpenings = numOpenings > 0 && numOpenings <= 3;
  const showNumSeatsFilled = numOpenings > 0;

  return (
    <TableCell align="right" sx={{ opacity: 1 }}>
      {isOngoing ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            rowGap: 4
          }}
        >
          <Tooltip
            placement="top"
            title={ctaDisabled ? ctaDisabledMessage : null}
          >
            <Box>
              <CtaComponent section={section} disabled={ctaDisabled} />
            </Box>
          </Tooltip>
          <Box
            sx={{
              textAlign: "center",
              width: "100%"
            }}
          >
            {!hideSeats && section.size_max != 1 && (
              <>
                {showNumSeatsFilled && (
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      display: "block"
                    }}
                  >
                    {t("{{numSeatsFilled}} of {{maxCapacity}} seats filled", {
                      numSeatsFilled: section.filledSpaceCount,
                      maxCapacity: section.size_max,
                      count: section.filledSpaceCount,
                      defaultValue_plural:
                        "{{numSeatsFilled}} of {{maxCapacity}} seats filled"
                    })}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={(theme: Theme) => ({
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: "1em",
            rowGap: "0.5em",
            [theme.breakpoints.down("lg")]: {
              flexDirection: "column-reverse",
              alignItems: "flex-end"
            }
          })}
        >
          <Box
            sx={(theme: Theme) => ({
              textAlign: "left",
              [theme.breakpoints.down("lg")]: {
                textAlign: "right"
              }
            })}
          >
            {!hideSeats && section.size_max != 1 && (
              <>
                {showNumSeatsFilled && (
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      display: "block"
                    }}
                  >
                    {t("{{numSeatsFilled}} seat filled", {
                      numSeatsFilled: section.filledSpaceCount,
                      count: section.filledSpaceCount,
                      defaultValue_plural: "{{numSeatsFilled}} seats filled"
                    })}
                  </Typography>
                )}
                {showNumOpenings && (
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      color: "error.700",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {t("Only {{numOpenings}} seat left!", {
                      numOpenings,
                      count: numOpenings,
                      defaultValue_plural: "Only {{numOpenings}} seats left!"
                    })}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Tooltip
            placement="top"
            title={ctaDisabled ? ctaDisabledMessage : null}
          >
            <Box>
              <CtaComponent section={section} disabled={ctaDisabled} />
            </Box>
          </Tooltip>
        </Box>
      )}
    </TableCell>
  );
}

function TaughtBy({
  section,
  isSellerOrg,
  isTeacherAssigned,
  t,
  isOngoing
}: {
  section: SectionRowSection;
  isSellerOrg: boolean;
  isTeacherAssigned: boolean;
  t: TFunction;
  isOngoing?: boolean;
}) {
  const isTablet = useIsTablet();
  if (!isSellerOrg) {
    return null;
  }

  const sectionLeaderName = section.leader.name;

  if (isOngoing) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: isOngoing ? "flex-start" : "center",
          minWidth: 60,
          overflow: "hidden",
          textOverflow: "ellipsis",
          flexDirection: "column"
        }}
      >
        <Tooltip title={sectionLeaderName}>
          <Typography
            variant="body1"
            emphasized={true}
            noWrap={!isTablet}
            sx={
              isTablet
                ? {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: "2",
                    overflow: "hidden"
                  }
                : { maxWidth: "300px", display: "block" }
            }
          >
            {isTeacherAssigned
              ? sectionLeaderName
              : t("Teacher not yet assigned")}
          </Typography>
        </Tooltip>
        <Typography variant="body2">{t("Teacher")}</Typography>
      </Box>
    );
  } else {
    return (
      <Typography
        variant="body1"
        noWrap={!isTablet}
        sx={
          isTablet
            ? {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "2",
                overflow: "hidden"
              }
            : { maxWidth: "300px", display: "block" }
        }
      >
        {isTeacherAssigned
          ? t("Taught by: {{sectionLeaderName}}", { sectionLeaderName })
          : t("Teacher not yet assigned")}
      </Typography>
    );
  }
}
